import {
	makeBackwardsCompatibleMap,
	getApiName,
} from "../../convert-api-result-to-google-json/index.js";
import { debounce } from "../../../utils/utils.js";
import {
	createDropdown,
	updateDropdown,
	showDropdown,
	hideDropdown,
	createSuggestList,
} from "./dropdown.js";

/**
 * Get the visible dropdown option items for a given field.
 * Returns an empty array when the dropdown is hidden or absent.
 */
function getDropdownItems(field, dropdownClassname) {
	const dropdown = field.parentNode.querySelector(`.${dropdownClassname}`);
	if (!dropdown || dropdown.style.display === "none") {
		return [];
	}
	return dropdown.querySelectorAll('li[role="option"]');
}

/**
 * Visually highlight a dropdown item and announce it to screen readers.
 * Clears the previous highlight first, then marks the new active item.
 */
function highlightItem(field, items, index) {
	items.forEach((item) => item.classList.remove("is-active"));
	const active = items[index];
	if (!active) return;
	active.classList.add("is-active");
	field.setAttribute("aria-activedescendant", active.id);
	active.scrollIntoView({ block: "nearest" });
}

/**
 * Move the active highlight forward or backward through the dropdown items.
 * Wraps around in both directions (last → first, first → last).
 */
function moveHighlight(field, items, direction) {
	if (direction === "down") {
		field._activeIndex = (field._activeIndex + 1) % items.length;
	} else {
		field._activeIndex =
			field._activeIndex <= 0 ? items.length - 1 : field._activeIndex - 1;
	}
	highlightItem(field, items, field._activeIndex);
}

/**
 * Select the currently highlighted dropdown item by simulating a click.
 * The existing click handler in dropdown.js handles the rest
 * (API lookup, fill address, update map pin, etc).
 */
function selectHighlightedItem(field, items) {
	if (field._activeIndex >= 0 && items[field._activeIndex]) {
		items[field._activeIndex].click();
	}
}

/**
 * Handle keyboard events on the input for dropdown navigation.
 * - ArrowDown / ArrowUp: move through the options
 * - Enter: select the highlighted option
 * - Escape: close the dropdown
 */
function handleDropdownKeyboard(event, field, dropdownClassname) {
	const items = getDropdownItems(field, dropdownClassname);
	if (items.length === 0) return;

	switch (event.key) {
		case "ArrowDown":
			event.preventDefault();
			moveHighlight(field, items, "down");
			break;
		case "ArrowUp":
			event.preventDefault();
			moveHighlight(field, items, "up");
			break;
		case "Enter":
			if (field._activeIndex >= 0) {
				event.preventDefault();
				selectHighlightedItem(field, items);
			}
			break;
		case "Escape":
			hideDropdown({ field, dropdownClassname });
			break;
	}
}

// This is exported back to index.js, who will use it to handle input changes
export function inputChangeHandler(context) {
	const {
		apiSuggestUrl,
		suggestMaxResults,
		apiRequest,
		dropdownHandler,
		apiSuggestRowsParam,
		apiSuggestQueryParam,
	} = context;
	const navigationKeys = ["ArrowDown", "ArrowUp", "Enter", "Escape"];
	return async function (event) {
		// Ignore navigation keys — they are handled by the keydown listener in step 6.15
		if (event.key && navigationKeys.includes(event.key)) return;

		const field = event.target || event;
		// Step 7.0 - If the field turns empty, hide the dropdown
		if (field.value.trim() === "") {
			hideDropdown({ ...context, field });
			return;
		}
		// Step 7.1 - Create an empty dropdown to start with
		const suggestList = "";

		// Step 7.2 - The EVENT handler is actually created here inside createDropdown
		createDropdown({
			...context,
			field,
			suggestList,
			dropdownEventHandler: dropdownHandler,
		});

		// Step 8 - Fetch actual suggestions from the API
		const encodedQuery = encodeURIComponent(field.value);
		const finalSuggestUrl = `${apiSuggestUrl}?${apiSuggestRowsParam}${suggestMaxResults}&${apiSuggestQueryParam}${encodedQuery}`;
		const data = await apiRequest(finalSuggestUrl);

		// Step 9 - Build the suggestion list and update the dropdown
		const builtSuggestList = createSuggestList(data);
		updateDropdown({ ...context, field, suggestList: builtSuggestList });
		showDropdown({ ...context, field });
	};
}

// Step 5 - Add event listeners to the field
export function addEventListeners(field, inputHandler, dropdownClassname) {
	field.setAttribute("role", "combobox");
	field.setAttribute("aria-autocomplete", "list");
	field.setAttribute("aria-expanded", "false");

	// Step 6 - The listener that triggers the main logic
	field.addEventListener("keyup", debounce(inputHandler));

	// Step 6.1 - This seems to be required for when the input value gets deleted by mouse event clicking the Cross icon
	field.addEventListener("change", () => {
		if (field.value.trim() === "") {
			hideDropdown({ field, dropdownClassname });
		}
	});

	// Step 6.15 - Keyboard navigation for the dropdown (ArrowUp/Down, Enter, Escape)
	field._activeIndex = -1;
	field.addEventListener("keydown", (event) => {
		handleDropdownKeyboard(event, field, dropdownClassname);
	});

	// Step 6.2 - For when the user travels away from the field
	field.addEventListener("blur", () => {
		// Timeout required else it prevents clicks on the dropdown options, since that blurs the input
		setTimeout(() => {
			hideDropdown({ field, dropdownClassname });
		}, 150);
	});

	// Step 6.3 - Show dropdown again on focus
	field.addEventListener("focus", () => {
		showDropdown({ field, dropdownClassname });
	});
}

export function forceUpdateInputValueWithoutOpeningDropdown(field, doc) {
	const address = getApiName("address", "pdok");
	if (!field || !doc || !doc[address] || doc[address] === "Laden...") return;

	field.value = doc[address];

	/* Also fill the hidden input with Google-style JSON data */
	const prevElem = field.previousElementSibling;
	if (!prevElem || prevElem.tagName !== "INPUT") return;
	const googleStyleObj = makeBackwardsCompatibleMap(doc, "pdok");
	prevElem.value = JSON.stringify(googleStyleObj);
}
